import React from 'react';
import { useFormContext } from 'react-hook-form';
import FormField from './FormField';
import CNICField from './CNICField';
import { COUNTRY_OPTIONS } from '../constants/countries';
import { EMPLOYMENT_TYPES } from '../constants';

const PHONE_VALIDATION = {
  pattern: {
    value: /^[+]?\d{10,15}$/,
    message: 'Phone must be a valid international number (e.g. +923001234567)'
  }
};

const EmployeeForm = ({
  departments = [], 
  employees = [], 
  paymentMethods = [],
  banks = [],
  mode = 'add', // 'add' or 'edit'
  showSalarySection = false,
  AddressField = null, // Custom address field component
  currentEmployeeId = null
}) => {
  const { register, watch } = useFormContext();
  const selectedPaymentMethod = watch('payment_method_id');
  const employmentStatus = watch('employment_status'); // Watch employment_status for conditional field
  const includeSalary = watch('include_salary'); // Watch checkbox to show/hide salary section
  const selectedMethod = paymentMethods.find(pm => pm.id === Number.parseInt(selectedPaymentMethod, 10));

  const genderOptions = [
    { value: 'Male', label: 'Male' },
    { value: 'Female', label: 'Female' },
    { value: 'Other', label: 'Other' }
  ];

  const employmentStatusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'terminated', label: 'Terminated' }
  ];

  const employmentTypeOptions = EMPLOYMENT_TYPES.map((type) => ({ value: type, label: type }));

  const departmentOptions = departments.map(dept => ({
    value: dept.id,
    label: dept.name
  }));

  const managerOptions = employees
    .filter(emp => (emp.employment_status || 'active') === 'active'
      && !['Intern', 'Trainee'].includes(emp.employment_type)
      && (mode === 'edit' ? emp.id !== currentEmployeeId : true))
    .map(emp => {
      const positionPart = emp.position ? ` (${emp.position})` : '';
      return { value: emp.id, label: `${emp.first_name} ${emp.last_name}${positionPart}` };
    });

  const paymentMethodOptions = paymentMethods.map(pm => ({
    value: pm.id,
    label: pm.name
  }));

  const bankOptions = banks.map(bank => ({
    value: bank.id,
    label: bank.name
  }));

  return (
    <div className="space-y-6">
      {/* Basic Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Basic Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            name="first_name"
            label="First Name"
            required={true}
            validation={{ required: 'First name is required' }}
            data-testid="employee-form-field-first-name"
          />
          <FormField
            name="last_name"
            label="Last Name"
            required={true}
            validation={{ required: 'Last name is required' }}
            data-testid="employee-form-field-last-name"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            name="email"
            label="Work Email"
            type="email"
            required={true}
            helpText="Company address, e.g. user@bytecorp.io"
            validation={{ required: 'Work email is required' }}
            data-testid="employee-form-field-email"
          />
          <FormField
            name="personal_email"
            label="Personal Email"
            type="email"
            helpText="Optional — used for login invites / recovery"
            validation={{
              pattern: {
                value: /^\S+@\S+\.\S+$/,
                message: 'Invalid email format'
              }
            }}
            data-testid="employee-form-field-personal-email"
          />
        </div>

        <CNICField required={true} data-testid="employee-form-field-cnic" />

        <FormField
          name="phone"
          label="Phone"
          helpText="e.g. +923001234567"
          validation={PHONE_VALIDATION}
          data-testid="employee-form-field-phone"
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            name="date_of_birth"
            label="Date of Birth"
            type="date"
            data-testid="employee-form-field-date-of-birth"
          />
          <FormField
            name="gender"
            label="Gender"
            type="select"
            options={genderOptions}
            data-testid="employee-form-field-gender"
          />
        </div>

        {AddressField ? (
          <AddressField />
        ) : (
          <FormField
            name="address"
            label="Address"
            required={mode === 'add'}
            validation={mode === 'add' ? { required: 'Address is required' } : {}}
            data-testid="employee-form-field-address"
          />
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            name="city"
            label="City"
            data-testid="employee-form-field-city"
          />
          <FormField
            name="country"
            label="Country"
            type="select"
            searchable
            options={COUNTRY_OPTIONS}
            placeholder="Select country..."
            data-testid="employee-form-field-country"
          />
        </div>
      </div>

      {/* Employment Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Employment Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            name="position"
            label="Position"
            required={true}
            validation={{ required: 'Position is required' }}
            data-testid="employee-form-field-position"
          />
          <FormField
            name="department_id"
            label="Department"
            type="select"
            required={true}
            options={departmentOptions}
            validation={{ required: 'Department is required' }}
            data-testid="employee-form-department"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            name="hire_date"
            label="Hire Date"
            type="date"
            required={true}
            helpText="DD-MM-YYYY"
            validation={{ required: 'Hire date is required' }}
            data-testid="employee-form-field-hire-date"
          />
          <FormField
            name="employment_type"
            label="Employment Type"
            type="select"
            required={true}
            options={employmentTypeOptions}
            validation={{ required: 'Employment type is required' }}
            data-testid="employee-form-field-employment-type"
          />
        </div>

        {mode === 'edit' && (
          <FormField
            name="employment_status"
            label="Employment Status"
            type="select"
            options={employmentStatusOptions}
            data-testid="employee-form-field-employment-status"
          />
        )}

        {/* Conditional Last Working Date field */}
        {mode === 'edit' && (employmentStatus === 'inactive' || employmentStatus === 'terminated') && (
          <FormField
            name="last_working_date"
            label="Last Working Date"
            type="date"
            required={true}
            helpText="Required when status is inactive or terminated"
            validation={{
              required: 'Last working date is required when status is inactive or terminated'
            }}
            data-testid="employee-form-field-last-working-date"
          />
        )}

        <FormField
          name="manager_id"
          label="Manager"
          type="select"
          options={[{ value: '', label: 'None' }, ...managerOptions]}
          searchable={true}
          placeholder="Search or select manager..."
          data-testid="employee-form-manager"
        />

        {/* Timesheet app fields. Only PUT /api/employees/:id accepts them, so edit mode only. */}
        {mode === 'edit' && (
          <>
            {/* A per-person exception to the attendance policy's target for this
                employment type. Blank is the normal case. */}
            <FormField
              name="daily_target_hours"
              label="Daily target hours exception"
              type="number"
              step="0.5"
              min="0"
              max="24"
              helpText="Blank uses the attendance policy for this employment type."
              validation={{
                min: { value: 0, message: 'Daily target hours cannot be negative' },
                max: { value: 24, message: 'Daily target hours cannot exceed 24' }
              }}
              data-testid="employee-form-field-daily-target-hours"
            />
            <div>
              <FormField
                name="attendance_exempt"
                label="Attendance exempt"
                type="checkbox"
                data-testid="employee-form-field-attendance-exempt"
              />
              <p className="text-xs text-gray-400 ml-6">No reminders, never late; record only</p>
            </div>
          </>
        )}
      </div>

      {/* Emergency Contact */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Emergency Contact</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
          <FormField
            name="emergency_contact_name"
            label="Name"
            data-testid="employee-form-field-emergency-contact-name"
          />
          <FormField
            name="emergency_contact_phone"
            label="Phone"
            data-testid="employee-form-field-emergency-contact-phone"
          />
          <FormField
            name="emergency_contact_relationship"
            label="Relationship"
            data-testid="employee-form-field-emergency-contact-relationship"
          />
        </div>
      </div>

      <FormField
        name="taxable"
        label="Taxable Employee"
        type="checkbox"
        data-testid="employee-form-field-taxable"
      />

      {/* Salary Section - Only for Add mode */}
      {showSalarySection && mode === 'add' && (
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="include_salary"
              {...register('include_salary')}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="include_salary" className="text-sm font-medium text-gray-700">
              Include salary information (optional - can be added later)
            </label>
          </div>
          
          {includeSalary && (
            <div className="space-y-4 border-t pt-4">
              <h3 className="text-lg font-semibold text-gray-900">Salary Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  name="amount"
                  label="Amount"
                  type="number"
                  step="0.01"
                  required={true}
                  validation={{ 
                    required: 'Amount is required when adding salary',
                    min: { value: 0, message: 'Amount must be greater than 0' }
                  }}
                />
                <FormField
                  name="currency"
                  label="Currency"
                  type="select"
                  required={true}
                  options={[
                    { value: 'USD', label: 'USD' },
                    { value: 'PKR', label: 'PKR' }
                  ]}
                  validation={{ required: 'Currency is required when adding salary' }}
                />
              </div>

              <FormField
                name="payment_method_id"
                label="Payment Method"
                type="select"
                required={true}
                options={paymentMethodOptions}
                validation={{ required: 'Payment method is required when adding salary' }}
              />

              {/* Conditional fields, each following its own payment-method flag, the
                  same rules as SalaryForm. The bank used to key on the method NAME
                  ('Local Bank Transfer'), so Cheque never asked for its bank, and the
                  title was nested under the number, so a title-only method never
                  asked for its title. */}
              {selectedMethod?.requires_bank_name && (
                <FormField
                  name="bank_id"
                  label="Bank"
                  type="select"
                  required={true}
                  options={bankOptions}
                  validation={{ required: 'Bank is required' }}
                  data-testid="employee-form-field-bank"
                />
              )}

              {(selectedMethod?.requires_account_number || selectedMethod?.requires_account_title) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {selectedMethod.requires_account_number && (
                    <FormField
                      name="account_number"
                      label="Account Number"
                      required={true}
                      validation={{ required: 'Account number is required' }}
                      data-testid="employee-form-field-account-number"
                    />
                  )}
                  {selectedMethod.requires_account_title && (
                    <FormField
                      name="account_title"
                      label="Account Title"
                      required={true}
                      validation={{ required: 'Account title is required' }}
                      data-testid="employee-form-field-account-title"
                    />
                  )}
                </div>
              )}

              <FormField
                name="effective_date"
                label="Effective Date"
                type="date"
                required={true}
                validation={{ required: 'Effective date is required when adding salary' }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * The edit form a department manager (employees:update_department without update_all)
 * gets: exactly DEPARTMENT_MANAGER_EDITABLE_FIELDS (utils/employeePermissions.js), with
 * the same labels, validation and test ids as the full form above. Every other field
 * belongs to HR, and the server refuses a body naming any of them.
 */
export const DepartmentManagerEmployeeFields = () => (
  <div className="space-y-6">
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Employment Information</h3>
      <FormField
        name="position"
        label="Position"
        required={true}
        validation={{ required: 'Position is required' }}
        data-testid="employee-form-field-position"
      />
    </div>

    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Contact Details</h3>
      <FormField
        name="phone"
        label="Phone"
        helpText="e.g. +923001234567"
        validation={PHONE_VALIDATION}
        data-testid="employee-form-field-phone"
      />
      <FormField name="address" label="Address" data-testid="employee-form-field-address" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField name="city" label="City" data-testid="employee-form-field-city" />
        <FormField
          name="country"
          label="Country"
          type="select"
          searchable
          options={COUNTRY_OPTIONS}
          placeholder="Select country..."
          data-testid="employee-form-field-country"
        />
      </div>
    </div>

    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Emergency Contact</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
        <FormField name="emergency_contact_name" label="Name" data-testid="employee-form-field-emergency-contact-name" />
        <FormField name="emergency_contact_phone" label="Phone" data-testid="employee-form-field-emergency-contact-phone" />
        <FormField
          name="emergency_contact_relationship"
          label="Relationship"
          data-testid="employee-form-field-emergency-contact-relationship"
        />
      </div>
    </div>
  </div>
);

export default EmployeeForm; 